import { nanoid } from "nanoid";
import React from "react";

import { Editor } from "~/components/code/editor";
import { Tabs } from "~/components/tabs";
import { TabsContent } from "~/components/tabs/content";
import { TabsList } from "~/components/tabs/list";
import { TabsTrigger } from "~/components/tabs/trigger";
import { isEmpty } from "~/tools/is-empty";
import { isShallowEqual } from "~/tools/is-shallow-equal";
import type { Object } from "~/tools/object";
import { prop } from "~/tools/prop";
import { toEntries } from "~/tools/to-entries";
import { useMemo } from "~/tools/use-memo";

import { getRelationName } from "../../bottom_content/view/panes/east/element-details/ElementDetailsUtils";
import { TableRow } from "../../bottom_content/view/panes/east/element-details/modal-dialog/TableRow";
import type { FullComponent, FullRelation } from "../../common/openapi";
import { jsonToYaml } from "../../generic/yamlUtils";
import { ExplorationDrawer } from "../react/drawer/ExplorationDrawer";
import { SortedTagList } from "../react/sorted-tag-list/SortedTagList";

import { ElementDrawerTitle } from "./element-drawer-title";
import { GeneralPropertiesTabLayout } from "./general-properties-tab";

const ELEMENT_DEFAULT_TAB_VALUE = nanoid();

export interface ElementPropertiesDrawerProps {
	when: undefined | FullComponent | FullRelation;
}

export const ElementPropertiesDrawer: React.FC<ElementPropertiesDrawerProps> = ({ when }) => (
	<ExplorationDrawer
		headerTitle={(fullElement) => <ElementDrawerTitle fullElement={fullElement} typeOfData="properties" />}
		when={when}
	>
		{(fullElement) =>
			"fields" in fullElement ? (
				<ComponentProperties fullComponent={fullElement} />
			) : (
				<RelationProperties fullRelation={fullElement} />
			)
		}
	</ExplorationDrawer>
);

const ComponentProperties: React.FC<{
	fullComponent: FullComponent;
}> = ({ fullComponent }) => {
	const properties = useMemo(
		() => toEntries(fullComponent.data.properties).map(([key, value]) => `${key}:${value}`),
		[fullComponent.data.properties],
	);

	return (
		<Tabs className="flex h-full flex-col gap-4" defaultValue={ELEMENT_DEFAULT_TAB_VALUE}>
			<TabsList className="justify-start overflow-x-auto">
				<TabsTrigger value={ELEMENT_DEFAULT_TAB_VALUE}>Component</TabsTrigger>

				{fullComponent.synced.map(({ syncName }) => (
					<TabsTrigger key={syncName} value={syncName}>
						{syncName}
					</TabsTrigger>
				))}
			</TabsList>

			<TabsContent className="min-h-0 flex-1" value={ELEMENT_DEFAULT_TAB_VALUE}>
				<GeneralPropertiesTabLayout
					componentTypeName={fullComponent.typeName}
					description={fullComponent.data.description}
					id={fullComponent.data.id}
					identifiers={fullComponent.data.identifiers}
					labels={fullComponent.data.tags}
					lastUpdateTimestamp={fullComponent.data.lastUpdateTimestamp}
					name={fullComponent.data.name}
					syncNames={fullComponent.synced.map(prop("syncName"))}
					version={fullComponent.data.version}
				>
					<TableRow label="Layer">{fullComponent.data.layerName}</TableRow>

					<TableRow label="Domain">{fullComponent.data.domainName}</TableRow>

					<TableRow label="Properties">
						<SortedTagList items={properties} />
					</TableRow>
				</GeneralPropertiesTabLayout>
			</TabsContent>

			{fullComponent.synced.map(({ syncName, elementTypeTag, identifiers, tags, sourceProperties, status, data }) => (
				<TabsContent className="flex-1" key={syncName} value={syncName}>
					<ExternalSourceYaml
						properties={{
							type: elementTypeTag,
							identifiers,
							tags,
							configuration: sourceProperties,
							status,
							data: isEmpty(data) ? undefined : data,
						}}
					/>
				</TabsContent>
			))}
		</Tabs>
	);
};

const RelationProperties: React.FC<{
	fullRelation: FullRelation;
}> = ({ fullRelation }) => (
	<Tabs className="flex h-full flex-col gap-4" defaultValue={ELEMENT_DEFAULT_TAB_VALUE}>
		<TabsList className="justify-start overflow-x-auto">
			<TabsTrigger value={ELEMENT_DEFAULT_TAB_VALUE}>Relation</TabsTrigger>

			{fullRelation.synced.map(({ syncName }) => (
				<TabsTrigger key={syncName} value={syncName}>
					{syncName}
				</TabsTrigger>
			))}
		</TabsList>

		<TabsContent className="min-h-0 flex-1" value={ELEMENT_DEFAULT_TAB_VALUE}>
			<GeneralPropertiesTabLayout
				componentTypeName={fullRelation.typeName}
				description={fullRelation.data.description}
				id={fullRelation.data.id}
				identifiers={fullRelation.data.identifiers}
				labels={fullRelation.data.tags}
				lastUpdateTimestamp={fullRelation.data.lastUpdateTimestamp}
				name={getRelationName(fullRelation, fullRelation.typeName)}
				syncNames={fullRelation.synced.map(prop("syncName"))}
			/>
		</TabsContent>

		{fullRelation.synced.map(({ syncName, elementTypeTag, identifiers, tags, data }) => (
			<TabsContent className="flex-1" key={syncName} value={syncName}>
				<ExternalSourceYaml
					properties={{
						type: elementTypeTag,
						identifiers,
						tags,
						data: isEmpty(data) ? undefined : data,
					}}
				/>
			</TabsContent>
		))}
	</Tabs>
);

const ExternalSourceYaml: React.FC<{
	properties: Object;
}> = ({ properties }) => {
	const yaml = useMemo(() => jsonToYaml(properties, { sort: false }), [properties], [isShallowEqual]);

	return <Editor height="100%" language="yaml" readOnly value={yaml} />;
};
